import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { puede } from "../../../../permisos/permisos";

// Recordatorio: vigila a los clientes marcados como frecuentes y avisa cuando llevan
// mas de X dias sin ningun movimiento de venta (cotizacion, pedido o entrega).
function ReporteRecordatorio() {
  const { user } = useContext(AuthContext);
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const headers = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  const [filas, setFilas] = useState([]);
  // Dias sin movimiento a partir de los cuales se alerta. Se recuerda por navegador.
  const [dias, setDias] = useState(Number(localStorage.getItem("recordatorioDias")) || 30);
  const [filtro, setFiltro] = useState("alertas");
  const [cargando, setCargando] = useState(true);

  // Modal para elegir que clientes se vigilan.
  const [modal, setModal] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [guardando, setGuardando] = useState(false);

  const hoy = moment().format("YYYY-MM-DD");

  useEffect(() => {
    cargaRecordatorio();
  }, []);

  function cargaRecordatorio() {
    setCargando(true);
    axios
      .get(`${URL_CLIENTES}Recordatorio`, headers)
      .then((r) => {
        setFilas(r.data || []);
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
      });
  }

  function abreClientes() {
    axios
      .get(URL_CLIENTES, headers)
      .then((r) => {
        setClientes(
          (r.data || [])
            .filter((c) => c.is_active === "Si")
            .map((c) => ({
              _id: c._id,
              codigo: c.codigo || "",
              nombre: c.nombre_comercial || "",
              frecuente: c.frecuente === "Si",
              original: c.frecuente === "Si",
            }))
            .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
        );
        setBuscar("");
        setModal(true);
      })
      .catch((e) => console.log(e));
  }

  function toggleCliente(id) {
    setClientes((prev) => prev.map((c) => (c._id === id ? { ...c, frecuente: !c.frecuente } : c)));
  }

  // Solo se mandan los que cambiaron.
  function guardaClientes() {
    const cambios = clientes.filter((c) => c.frecuente !== c.original);
    if (!cambios.length) {
      setModal(false);
      return;
    }
    setGuardando(true);
    Promise.all(
      cambios.map((c) =>
        axios.patch(`${URL_CLIENTES}/${c._id}`, { frecuente: c.frecuente ? "Si" : "No" }, headers)
      )
    )
      .then(() => {
        setGuardando(false);
        setModal(false);
        Swal.fire("Listo", `${cambios.length} cliente(s) actualizados`, "success");
        cargaRecordatorio();
      })
      .catch((error) => {
        setGuardando(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "No se pudieron guardar los cambios",
          footer: `${error.response ? JSON.stringify(error.response.data) : error}`,
        });
      });
  }

  const clientesFiltrados = clientes.filter((c) => {
    if (!buscar) return true;
    const t = buscar.toLowerCase();
    return c.nombre.toLowerCase().includes(t) || c.codigo.toString().toLowerCase().includes(t);
  });

  // Calculo de dias sin movimiento y limite aplicable a cada cliente.
  const data = filas
    .map((c) => {
      const limite = c.frecuenciaCompra > 0 ? c.frecuenciaCompra : Number(dias) || 0;
      const sinMovimiento = c.ultimoMovimiento ? moment(hoy).diff(moment(c.ultimoMovimiento), "days") : null;
      return {
        ...c,
        limite,
        sinMovimiento,
        // Sin movimientos registrados tambien es alerta.
        alerta: sinMovimiento === null || sinMovimiento >= limite,
      };
    })
    .sort((a, b) => {
      const va = a.sinMovimiento === null ? 99999 : a.sinMovimiento;
      const vb = b.sinMovimiento === null ? 99999 : b.sinMovimiento;
      return vb - va;
    });

  const dataFiltrada = data.filter((c) => (filtro === "alertas" ? c.alerta : true));
  const totalAlertas = data.filter((c) => c.alerta).length;

  const badge = (c) => {
    if (c.sinMovimiento === null)
      return <span className="badge badge-secondary">Sin movimientos</span>;
    if (c.alerta)
      return <span className="badge badge-danger">Marcar — {c.sinMovimiento} días sin movimiento</span>;
    return (
      <span className="badge badge-success">
        Al corriente — faltan {c.limite - c.sinMovimiento} días
      </span>
    );
  };

  function PDFTabla() {
    const doc = new jsPDF("landscape");
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 240, 10, 45, 15);
    doc.setFontSize(14);
    doc.text("Recordatorio de Clientes Frecuentes", 14, 18);
    doc.setFontSize(11);
    doc.text(`Fecha: ${hoy}  |  Alerta a los ${dias} días sin movimiento`, 14, 25);
    doc.autoTable({
      head: [[
        "Código", "Cliente", "Teléfono", "Vendedor", "Últ. cotización", "Últ. pedido",
        "Últ. venta", "Último movimiento", "Días sin mover", "Límite",
      ]],
      body: dataFiltrada.map((c) => [
        c.codigo,
        c.nombre_comercial,
        c.telefono,
        c.vendedor,
        c.ultimaCotizacion || "-",
        c.ultimoPedido || "-",
        c.ultimaVenta || "-",
        c.ultimoMovimiento ? `${c.ultimoMovimiento} (${c.tipoUltimoMovimiento})` : "Nunca",
        c.sinMovimiento === null ? "-" : c.sinMovimiento,
        c.limite,
      ]),
      startY: 30,
      styles: { fontSize: 8 },
    });
    window.open(doc.output("bloburl"), "_blank");
  }

  function excel() {
    const datos = dataFiltrada.map((c) => ({
      Codigo: c.codigo,
      Cliente: c.nombre_comercial,
      Telefono: c.telefono,
      Vendedor: c.vendedor,
      UltimaCotizacion: c.ultimaCotizacion || "-",
      UltimoPedido: c.ultimoPedido || "-",
      UltimaVenta: c.ultimaVenta || "-",
      UltimoMovimiento: c.ultimoMovimiento || "Nunca",
      TipoUltimoMovimiento: c.tipoUltimoMovimiento || "-",
      DiasSinMovimiento: c.sinMovimiento === null ? "-" : c.sinMovimiento,
      LimiteDias: c.limite,
    }));
    new ExportJsonExcel({
      fileName: "RecordatorioClientes",
      datas: [{ sheetData: datos, sheetName: "Recordatorio" }],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <div className="container">
        {puede(user, "ventas.recordatorio.ver") || puede(user, "ventas.recordatorio.ver") || puede(user, "ventas.recordatorio.ver") || puede(user, "ventas.recordatorio.ver") ? (
          <div className="card col-12">
            <div className="page-header">
              <div>
                <h3 className="page-title">
                  <i className="fas fa-phone-volume" style={{ marginRight: 8, color: "#10b981" }} />
                  Recordatorio de Clientes
                </h3>
                <p className="page-subtitle">
                  Clientes frecuentes sin cotización, pedido ni venta en los últimos {dias} días
                </p>
              </div>
              <div className="page-actions">
                <ButtonGroup>
                  {puede(user, "ventas.recordatorio.frecuentes") && (
<Button size="sm" color="success" onClick={abreClientes}>
                    <i className="fas fa-user-check" style={{ marginRight: 6 }} /> Clientes frecuentes
                  </Button>
)}
                  <Button size="sm" color="danger" onClick={PDFTabla}>
                    PDF <i className="far fa-file-pdf"></i>
                  </Button>
                  <Button size="sm" color="primary" onClick={excel}>
                    Excel <i className="far fa-file-excel"></i>
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <Row className="align-items-end">
              <Col md={3}>
                <Label>Días sin movimiento para alertar</Label>
                <Input
                  bsSize="sm"
                  type="number"
                  min={1}
                  value={dias}
                  onChange={(e) => {
                    setDias(e.target.value);
                    localStorage.setItem("recordatorioDias", e.target.value);
                  }}
                />
              </Col>
              <Col md={3}>
                <Label>Mostrar</Label>
                <Input bsSize="sm" type="select" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                  <option value="alertas">Solo por marcar</option>
                  <option value="">Todos los frecuentes</option>
                </Input>
              </Col>
              <Col md={3}>
                <h5 style={{ marginBottom: 0 }}>
                  Por marcar{" "}
                  <span className="badge badge-danger" style={{ fontSize: "0.9rem" }}>
                    {totalAlertas}
                  </span>{" "}
                  de {data.length}
                </h5>
              </Col>
            </Row>
            <br />
            <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
              <thead>
                <tr>
                  <th className="tituloTabla">Código</th>
                  <th className="tituloTabla">Cliente</th>
                  <th className="tituloTabla">Teléfono</th>
                  <th className="tituloTabla">Vendedor</th>
                  <th className="tituloTabla">Últ. cotización</th>
                  <th className="tituloTabla">Últ. pedido</th>
                  <th className="tituloTabla">Últ. venta</th>
                  <th className="tituloTabla">Días sin mover</th>
                  <th className="tituloTabla">Límite</th>
                  <th className="tituloTabla">Estado</th>
                </tr>
              </thead>
              <tbody>
                {dataFiltrada.map((c) => (
                  <tr key={c._id}>
                    <td>{c.codigo}</td>
                    <td>{c.nombre_comercial}</td>
                    <td>{c.telefono}</td>
                    <td>{c.vendedor}</td>
                    <td>{c.ultimaCotizacion || "-"}</td>
                    <td>{c.ultimoPedido || "-"}</td>
                    <td>{c.ultimaVenta || "-"}</td>
                    <td align="center">{c.sinMovimiento === null ? "-" : c.sinMovimiento}</td>
                    <td align="center">
                      {c.limite}
                      {c.frecuenciaCompra > 0 ? (
                        <small className="text-muted" title="Límite propio del cliente">
                          {" "}
                          *
                        </small>
                      ) : null}
                    </td>
                    <td>{badge(c)}</td>
                  </tr>
                ))}
                {dataFiltrada.length === 0 && (
                  <tr>
                    <td colSpan={10} align="center">
                      {cargando
                        ? "Cargando..."
                        : data.length === 0
                        ? "Todavía no marcas clientes frecuentes — usa el botón Clientes frecuentes"
                        : "Ningún cliente frecuente pasó el límite de días"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <small className="text-muted">
              * El cliente tiene su propia frecuencia de compra capturada y se respeta en lugar del límite general.
            </small>
          </div>
        ) : undefined}
      </div>

      <Modal size="lg" isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>
          <h4>Clientes frecuentes a vigilar</h4>
        </ModalHeader>
        <ModalBody>
          <Input
            type="text"
            placeholder="Buscar cliente..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
          <small className="text-muted">
            {clientes.filter((c) => c.frecuente).length} marcados de {clientes.length} clientes
          </small>
          <div
            style={{
              marginTop: 8,
              maxHeight: 420,
              overflowY: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: 8,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "2px 12px",
            }}
          >
            {clientesFiltrados.map((c) => (
              <label
                key={c._id}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0", margin: 0, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={c.frecuente}
                  onChange={() => toggleCliente(c._id)}
                  style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }}
                />
                <span>{c.nombre}</span>
                <small className="text-muted">{c.codigo}</small>
              </label>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={() => setModal(false)}>
            Cancelar
          </Button>
          <Button color="success" onClick={guardaClientes} disabled={guardando}>
            Guardar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ReporteRecordatorio;
